/**
 * KeyRestorePrompt — shown on Dashboard mount when the server has a
 * key backup on file AND this device has no locally-stored private key
 * (so the user is either on a fresh device or wiped by panic button).
 *
 * Offers the user two paths: restore now, or dismiss until next session
 * (we persist "dismissed" on a sessionStorage flag so they can keep
 * using the app with a freshly-generated keypair without nagging).
 */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { KeyRound, Loader2, RefreshCw, X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { getKeyBackupStatus, restoreKeyBackup } from '../utils/keyBackup';

const SESSION_FLAG = 'ghostcomm_restore_dismissed';

export const KeyRestorePrompt = () => {
  const [status, setStatus] = useState(null);
  const [open, setOpen] = useState(false);
  const [pp, setPp] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    // Duress mode: NEVER probe key-backup status. Doing so leaks to
    // the server (and any coerced observer) that an authenticated
    // session is active and reveals server-side backup state.
    if (sessionStorage.getItem('gs_duress_mode') === '1') return;
    // Only show if: server says backup exists AND this device just
    // generated a fresh keypair (indicated by recent `ghostcomm_keys`
    // without any `ghostcomm_seen_fingerprints_*` trust ledger entries
    // AND no onboarding flag). That's the clean "new device" tell.
    const dismissed = sessionStorage.getItem(SESSION_FLAG);
    if (dismissed) return;

    let hasTrust = false;
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith('ghostcomm_seen_fingerprints_')) {
        hasTrust = true;
        break;
      }
    }
    if (hasTrust) return; // already a "seasoned" device

    (async () => {
      try {
        const s = await getKeyBackupStatus();
        if (s?.exists) {
          setStatus(s);
          setOpen(true);
        }
      } catch {
        /* swallow — no backup, no prompt */
      }
    })();
  }, []);

  const dismiss = () => {
    sessionStorage.setItem(SESSION_FLAG, '1');
    setOpen(false);
  };

  const submit = async () => {
    setBusy(true);
    try {
      const summary = await restoreKeyBackup(pp);
      toast.success(
        `Keys restored. ${summary.trust_entries} trusted contact${
          summary.trust_entries === 1 ? '' : 's'
        } recovered. Reloading…`
      );
      // Short delay so the toast is visible, then hard reload so
      // AuthContext re-initializes with the restored keypair.
      setTimeout(() => window.location.reload(), 1200);
    } catch (e) {
      toast.error(e?.message || 'Restore failed');
      setBusy(false);
    }
  };

  if (!status?.exists) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && dismiss()}>
      <DialogContent className="max-w-md" data-testid="key-restore-prompt">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="w-5 h-5 text-primary" />
            Restore from another device?
          </DialogTitle>
          <DialogDescription className="pt-2 leading-relaxed">
            This account has a sync backup on file from{' '}
            {status?.updated_at
              ? new Date(status.updated_at).toLocaleString()
              : 'another session'}
            . Enter your sync passphrase to restore your private key + trust
            ledger here. Skip this if you'd rather start fresh on this device
            (you'll generate a new identity).
          </DialogDescription>
        </DialogHeader>

        <div className="pt-3 space-y-3">
          <label className="text-xs text-slate-400 block">
            Sync passphrase
          </label>
          <Input
            type="password"
            autoFocus
            value={pp}
            onChange={(e) => setPp(e.target.value)}
            placeholder="Not your account password"
            onKeyDown={(e) => e.key === 'Enter' && pp && submit()}
            data-testid="key-restore-passphrase-input"
            autoComplete="off"
          />
        </div>

        <DialogFooter className="pt-4">
          <Button
            variant="ghost"
            onClick={dismiss}
            disabled={busy}
            data-testid="key-restore-skip-btn"
          >
            <X className="w-3.5 h-3.5 mr-1.5" />
            Skip
          </Button>
          <Button
            onClick={submit}
            disabled={busy || !pp}
            data-testid="key-restore-submit-btn"
          >
            {busy ? (
              <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
            ) : (
              <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
            )}
            Restore
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default KeyRestorePrompt;
